import React, { useState, useEffect, useRef } from 'react';
import {
  Send,
  Copy,
  Check,
  Eye,
  Code,
  Layout,
  Phone,
  Share2,
  Megaphone,
  FileText,
  AlertCircle,
  CheckCircle2,
  Upload,
  RefreshCw,
  Bookmark,
  Layers,
  Save,
  Trash2,
  Plus,
  Loader2,
  Download,
  Building,
  Users,
} from 'lucide-react';
import {
  WorkspaceUser,
  TemplateId,
  SignatureEmployeeData,
  SignatureFieldToggles,
  SocialItem,
  PlanType,
  SavedSignatureTemplate,
  UserGroup,
} from '../types';
import {
  generateClientSignatureHtml,
  DEFAULT_CEREBRO_LOGO,
  downloadSignatureHtmlFile,
  copySignatureToClipboard,
} from '../services/signatureTemplateClient';
import { injectMySignature } from '../services/api';
import { getStoredGoogleAccessToken, loginWithGoogle, uploadAsset } from '../services/firebase';
import {
  saveSignatureToFirestore,
  loadSignatureFromFirestore,
} from '../services/signaturePersistenceService';

interface SignatureEditorProps {
  activeUser: WorkspaceUser | null;
  domain: string;
  currentPlan: PlanType;
  googleAccessToken: string | null;
  savedTemplates?: SavedSignatureTemplate[];
  groups?: UserGroup[];
  onSignatureInjected?: (userEmail: string) => void;
  onSaveTemplate?: (template: SavedSignatureTemplate) => void;
  onApplyTemplateToGroup?: (groupId: string, templateId: string) => void;
  onApplyTemplateToUser?: (userId: string, templateId: string) => void;
  onApplyTemplateToAll?: (templateId: string) => void;
  onUpdateUserAvatar?: (userId: string, avatarUrl: string) => void;
}


const COLOR_PALETTE = [
  { name: 'Cerebro Sky', value: '#0284c7' },
  { name: 'Emerald Green', value: '#10b981' },
  { name: 'Purple Royal', value: '#8b5cf6' },
  { name: 'Amber Gold', value: '#f59e0b' },
  { name: 'Crimson Rose', value: '#ec4899' },
  { name: 'Teal Modern', value: '#0d9488' },
  { name: 'Slate Dark', value: '#334155' },
];

const AVAILABLE_SOCIAL_PLATFORMS = ['linkedin', 'facebook', 'twitter', 'github', 'instagram', 'youtube'];

interface ImageUploadCardProps {
  label: string;
  description?: string;
  currentUrl?: string;
  fallbackUrl?: string;
  isUploading: boolean;
  onFileSelect: (file: File) => void;
  onClear: () => void;
  clearButtonText?: string;
  previewShape?: 'circle' | 'rounded' | 'square' | 'banner';
}

const ImageUploadCard: React.FC<ImageUploadCardProps> = ({
  label,
  description,
  currentUrl,
  fallbackUrl,
  isUploading,
  onFileSelect,
  onClear,
  clearButtonText = 'Usuń',
  previewShape = 'rounded',
}) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const displayUrl = currentUrl || fallbackUrl;
  const isCustom = Boolean(currentUrl);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) {
      onFileSelect(file);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const borderRadius =
    previewShape === 'circle' ? '50%' : previewShape === 'square' ? '0px' : '8px';

  return (
    <div
      style={{
        background: isDragOver ? 'rgba(0, 102, 255, 0.08)' : '#ffffff',
        border: `1.5px dashed ${isDragOver ? '#0066FF' : '#cbd5e1'}`,
        borderRadius: 10,
        padding: 14,
        transition: 'all 0.2s ease',
      }}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, flexWrap: 'wrap', gap: 8 }}>
        <div>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#0B192C' }}>{label}</div>
          {description && <div style={{ fontSize: 10, color: '#475569' }}>{description}</div>}
        </div>
        {isCustom && (
          <button
            type="button"
            onClick={onClear}
            className="btn"
            style={{
              padding: '3px 8px',
              fontSize: 10,
              background: 'rgba(239, 68, 68, 0.12)',
              color: '#f87171',
              border: '1px solid rgba(239, 68, 68, 0.25)',
              borderRadius: 6,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 4,
            }}
          >
            <Trash2 size={11} />
            <span>{clearButtonText}</span>
          </button>
        )}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        {/* Preview thumbnail */}
        {displayUrl && (
          <div
            style={{
              width: previewShape === 'banner' ? 120 : 54,
              height: previewShape === 'banner' ? 38 : 54,
              borderRadius,
              overflow: 'hidden',
              border: '1px solid #e2e8f0',
              background: '#f8fafc',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <img
              src={displayUrl}
              alt={label}
              style={{
                width: '100%',
                height: '100%',
                objectFit: previewShape === 'banner' ? 'cover' : 'contain',
                display: 'block',
              }}
            />
          </div>
        )}

        {/* Upload Action */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
          <input
            type="file"
            ref={fileInputRef}
            accept="image/*"
            style={{ display: 'none' }}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) onFileSelect(file);
              e.target.value = '';
            }}
          />

          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading}
            className="btn btn-outline"
            style={{
              fontSize: 11,
              padding: '7px 12px',
              justifyContent: 'center',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              width: '100%',
              cursor: isUploading ? 'wait' : 'pointer',
              borderColor: '#cbd5e1',
              color: '#0B192C',
              background: '#f8fafc',
              fontWeight: 600,
            }}
          >
            {isUploading ? (
              <>
                <Loader2 size={13} className="animate-spin" />
                <span>Przesyłanie do Firebase Storage...</span>
              </>
            ) : (
              <>
                <Upload size={13} />
                <span>Wybierz lub upuść grafikę</span>
              </>
            )}
          </button>

          <div style={{ fontSize: 9.5, color: '#64748b', textAlign: 'center' }}>
            Auto-hosting Firebase Storage: <code style={{ background: '#f1f5f9', color: '#0066FF', padding: '1px 5px', borderRadius: 4 }}>/tenants/{'{orgId}'}/assets/</code>
          </div>
        </div>
      </div>
    </div>
  );
};

export const SignatureEditor: React.FC<SignatureEditorProps> = ({
  activeUser,
  domain,
  currentPlan,
  googleAccessToken,
  savedTemplates = [],
  groups = [],
  onSignatureInjected,
  onSaveTemplate,
  onApplyTemplateToGroup,
  onApplyTemplateToUser,
  onApplyTemplateToAll,
}) => {
  // Active sub-tab in the editor panel
  const [editorTab, setEditorTab] = useState<'template' | 'fields' | 'contact' | 'socials' | 'banner' | 'legal'>('template');

  // Preview Mode
  const [previewMode, setPreviewMode] = useState<'visual' | 'code'>('visual');
  const [copied, setCopied] = useState(false);

  // Customization State
  const [templateId, setTemplateId] = useState<TemplateId>('modern_horizontal');
  const [primaryColor, setPrimaryColor] = useState('#0284c7');

  // Saved templates and modals state
  const [activeSavedTemplateId, setActiveSavedTemplateId] = useState<string>(savedTemplates[0]?.id || 'tpl_default');
  const [isSaveModalOpen, setIsSaveModalOpen] = useState(false);
  const [isApplyModalOpen, setIsApplyModalOpen] = useState(false);
  const [templateNameInput, setTemplateNameInput] = useState('');
  const [selectedApplyTarget, setSelectedApplyTarget] = useState<'group' | 'all' | 'user'>('group');
  const [selectedApplyGroupId, setSelectedApplyGroupId] = useState<string>(groups[0]?.id || 'group_main');
  const [feedbackNotice, setFeedbackNotice] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Synchronize when savedTemplates prop updates
  useEffect(() => {
    if (savedTemplates.length > 0 && !savedTemplates.some((t) => t.id === activeSavedTemplateId)) {
      setActiveSavedTemplateId(savedTemplates[0].id);
    }
  }, [savedTemplates]);

  // Dirty tracking & Firestore persistence state
  const [isDirty, setIsDirty] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingTemplate, setIsLoadingTemplate] = useState(false);

  // Storage uploads state
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [isUploadingBanner, setIsUploadingBanner] = useState(false);

  // Employee fields overrides
  const [fullName, setFullName] = useState(activeUser?.fullName || 'Jan Kowalski');
  const [jobTitle, setJobTitle] = useState(activeUser?.jobTitle || 'Lead Cloud Architect');
  const [department, setDepartment] = useState(activeUser?.department || 'Cloud Engineering');
  const [companyName, setCompanyName] = useState('CerebroLabs Sp. z o.o.');
  const [phone, setPhone] = useState(activeUser?.phone || '+48 22 123 45 67');
  const [email, setEmail] = useState(activeUser?.primaryEmail || `kontakt@${domain}`);
  const [websiteUrl, setWebsiteUrl] = useState(`https://${domain}`);
  const [nip, setNip] = useState('5252824941');
  const [regon, setRegon] = useState('386928102');
  const [address, setAddress] = useState('ul. Prosta 20, 00-850 Warszawa');
  const [logoUrl, setLogoUrl] = useState<string>(DEFAULT_CEREBRO_LOGO);
  const [logoWidth, setLogoWidth] = useState<number | undefined>(240);
  const [logoHeight, setLogoHeight] = useState<number | undefined>(68);
  const [avatarUrl, setAvatarUrl] = useState(activeUser?.avatarUrl || '');

  // Automatically measure logo natural dimensions when logoUrl changes and dimensions are unknown
  useEffect(() => {
    if (logoUrl && (!logoWidth || !logoHeight)) {
      const img = new Image();
      img.onload = () => {
        if (img.naturalWidth && img.naturalHeight) {
          setLogoWidth(img.naturalWidth);
          setLogoHeight(img.naturalHeight);
        }
      };
      img.src = logoUrl;
    }
  }, [logoUrl, logoWidth, logoHeight]);

  // Synchronize employee fields when activeUser changes
  // NOTE: avatarUrl belongs strictly to this employee/email!
  useEffect(() => {
    if (activeUser) {
      setFullName(activeUser.fullName || '');
      setJobTitle(activeUser.jobTitle || '');
      setDepartment(activeUser.department || '');
      setPhone(activeUser.phone || '');
      setEmail(activeUser.primaryEmail || '');
      setAvatarUrl(activeUser.avatarUrl || '');
    }
  }, [activeUser?.id, activeUser?.primaryEmail, activeUser?.avatarUrl]);

  // Field Toggles
  const [toggles, setToggles] = useState<SignatureFieldToggles>({
    showAvatar: true,
    avatarShape: 'circle',
    showJobTitle: true,
    showDepartment: true,
    showCompany: true,
    showPhone: true,
    showEmail: true,
    showWebsite: true,
    showTaxId: true,
    showAddress: true,
    showSocials: true,
    showBanner: true,
    showDisclaimer: true,
    showFreemiumBranding: currentPlan === 'free',
  });

  // Social Links with new structure: { platform, url, enabled }
  const [socialItems, setSocialItems] = useState<SocialItem[]>([
    { platform: 'linkedin', url: 'https://linkedin.com/company/cerebrolabs', enabled: true },
    { platform: 'facebook', url: 'https://facebook.com/cerebrolabs', enabled: true },
    { platform: 'twitter', url: 'https://x.com/cerebrolabs', enabled: true },
    { platform: 'github', url: 'https://github.com/cerebrolabs', enabled: true },
    { platform: 'instagram', url: '', enabled: false },
    { platform: 'youtube', url: '', enabled: false },
  ]);

  // Banner details
  const [bannerUrl, setBannerUrl] = useState('');
  const [bannerLink, setBannerLink] = useState(`https://${domain}`);
  const [bannerAlt, setBannerAlt] = useState('CerebroLabs Banner');
  const [promoTitle, setPromoTitle] = useState('⚡ Umów bezpłatną konsultację chmurową');
  const [promoText, setPromoText] = useState('Poznaj architekturę Google Cloud i zoptymalizuj koszty infrastruktury.');
  const [promoCtaText, setPromoCtaText] = useState('Sprawdź Ofertę →');

  // Legal disclaimer
  const [disclaimer, setDisclaimer] = useState(
    'Treść tej wiadomości zawiera informacje poufne przeznaczone wyłącznie dla adresata. Jeśli otrzymałeś tę wiadomość omyłkowo, prosimy o jej usunięcie i poinformowanie nadawcy.'
  );

  // Injection state
  const [isInjecting, setIsInjecting] = useState(false);
  const [injectionSuccess, setInjectionSuccess] = useState<string | null>(null);
  const [injectionError, setInjectionError] = useState<string | null>(null);

  // Helper to cleanly populate all editor fields from a specific template without cross-template pollution
  const applyTemplateToState = (tpl: SavedSignatureTemplate) => {
    setTemplateId(tpl.templateId || 'modern_horizontal');
    setPrimaryColor(tpl.primaryColor || '#0284c7');
    setToggles({
      showAvatar: tpl.toggles?.showAvatar ?? true,
      avatarShape: tpl.toggles?.avatarShape ?? 'circle',
      showJobTitle: tpl.toggles?.showJobTitle ?? true,
      showDepartment: tpl.toggles?.showDepartment ?? true,
      showCompany: tpl.toggles?.showCompany ?? true,
      showPhone: tpl.toggles?.showPhone ?? true,
      showEmail: tpl.toggles?.showEmail ?? true,
      showWebsite: tpl.toggles?.showWebsite ?? true,
      showTaxId: tpl.toggles?.showTaxId ?? true,
      showAddress: tpl.toggles?.showAddress ?? true,
      showSocials: tpl.toggles?.showSocials ?? true,
      showBanner: tpl.toggles?.showBanner ?? false,
      showDisclaimer: tpl.toggles?.showDisclaimer ?? true,
      showFreemiumBranding: currentPlan === 'free',
    });
    setCompanyName(tpl.companyName || 'CerebroLabs Sp. z o.o.');
    setPhone(tpl.companyPhone || '+48 22 123 45 67');
    setWebsiteUrl(tpl.websiteUrl || `https://${domain}`);
    setNip(tpl.nip || '5252824941');
    setRegon(tpl.regon || '386928102');
    setAddress(tpl.address || 'ul. Prosta 20, 00-850 Warszawa');
    setLogoUrl(
      tpl.logoUrl && !tpl.logoUrl.startsWith('data:image/svg')
        ? tpl.logoUrl
        : DEFAULT_CEREBRO_LOGO
    );
    setLogoWidth(tpl.logoWidth || 240);
    setLogoHeight(tpl.logoHeight || 68);
    setBannerUrl(tpl.bannerUrl || '');
    setBannerLink(tpl.bannerLink || `https://${domain}`);
    setBannerAlt(tpl.bannerAlt || 'CerebroLabs Banner');
    setPromoTitle(tpl.promoTitle || '');
    setPromoText(tpl.promoText || '');
    setPromoCtaText(tpl.promoCtaText || '');
    setDisclaimer(
      tpl.disclaimer ||
        'Treść tej wiadomości zawiera informacje poufne przeznaczone wyłącznie dla adresata. Jeśli otrzymałeś tę wiadomość omyłkowo, prosimy o jej usunięcie i poinformowanie nadawcy.'
    );
    if (tpl.socials) {
      if (Array.isArray(tpl.socials)) {
        setSocialItems(tpl.socials);
      } else {
        const converted: SocialItem[] = Object.entries(tpl.socials).map(([plat, u]) => ({
          platform: plat,
          url: u || '',
          enabled: Boolean(u),
        }));
        setSocialItems(converted);
      }
    } else {
      setSocialItems([
        { platform: 'linkedin', url: 'https://linkedin.com/company/cerebrolabs', enabled: true },
        { platform: 'facebook', url: 'https://facebook.com/cerebrolabs', enabled: true },
        { platform: 'twitter', url: 'https://x.com/cerebrolabs', enabled: true },
        { platform: 'github', url: 'https://github.com/cerebrolabs', enabled: true },
        { platform: 'instagram', url: '', enabled: false },
        { platform: 'youtube', url: '', enabled: false },
      ]);
    }
  };

  // 1. Startup load: Wczytuj zapisany stan przy starcie edytora z Firestore: organizations/{orgId}/signatures/{signatureId}
  useEffect(() => {
    let isMounted = true;
    async function loadInitial() {
      setIsLoadingTemplate(true);
      try {
        const loaded = await loadSignatureFromFirestore(domain, activeSavedTemplateId);
        if (loaded && isMounted) {
          applyTemplateToState(loaded);
          setIsDirty(false);
          setLastSavedAt(loaded.updatedAt ? new Date(loaded.updatedAt) : new Date());
        }
      } catch (err) {
        console.warn('Startup load signature warning:', err);
      } finally {
        if (isMounted) setIsLoadingTemplate(false);
      }
    }
    loadInitial();
    return () => {
      isMounted = false;
    };
  }, [domain, activeSavedTemplateId]);

  // Construct data object
  const currentSignatureData: SignatureEmployeeData = {
    fullName,
    jobTitle,
    department,
    companyName,
    phone,
    email,
    websiteUrl,
    nip,
    regon,
    address,
    logoUrl: logoUrl || DEFAULT_CEREBRO_LOGO,
    logoWidth,
    logoHeight,
    avatarUrl: avatarUrl || undefined,
    templateId,
    primaryColor,
    toggles: {
      ...toggles,
      showFreemiumBranding: currentPlan === 'free',
    },
    socials: socialItems,
    bannerUrl: bannerUrl || undefined,
    bannerLink,
    promoTitle,
    promoText,
    promoCtaText,
    disclaimer,
    plan: currentPlan,
  };

  const generatedHtml = generateClientSignatureHtml(currentSignatureData);

  const handleCopyHtml = async () => {
    try {
      await copySignatureToClipboard(generatedHtml);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Błąd kopiowania:', err);
    }
  };

  const handleDownloadHtml = () => {
    const filename = `stopka-${(companyName || 'cerebrosign').toLowerCase().replace(/[^a-z0-9]/g, '-')}.html`;
    downloadSignatureHtmlFile(generatedHtml, filename);
  };

  // Direct injection into Gmail API
  const handleDirectInjection = async () => {
    setIsInjecting(true);
    setInjectionSuccess(null);
    setInjectionError(null);

    let token = googleAccessToken || getStoredGoogleAccessToken();
    if (!token) {
      try {
        const res = await loginWithGoogle();
        token = res.accessToken;
      } catch (authErr: any) {
        console.warn('Google auth cancelled:', authErr);
        setInjectionError('Wymagana jest autoryzacja konta Google z uprawnieniem do Gmaila.');
        setIsInjecting(false);
        return;
      }
    }

    try {
      let res;
      try {
        res = await injectMySignature({
          signatureHtml: generatedHtml,
          userEmail: email,
          googleAccessToken: token,
          domain,
          plan: currentPlan,
          employeeData: currentSignatureData,
        });
      } catch (singleErr: any) {
        if (
          singleErr.message?.includes('BRAK_TOKENU_GOOGLE') ||
          singleErr.message?.includes('autoryzacji') ||
          singleErr.message?.includes('401')
        ) {
          const loginRes = await loginWithGoogle();
          token = loginRes.accessToken;
          if (token) {
            res = await injectMySignature({
              signatureHtml: generatedHtml,
              userEmail: email,
              googleAccessToken: token,
              domain,
              plan: currentPlan,
              employeeData: currentSignatureData,
            });
          } else {
            throw singleErr;
          }
        } else {
          throw singleErr;
        }
      }

      setInjectionSuccess(res?.message || `Stopka została pomyślnie zaktualizowana w Gmail dla ${email}!`);
      if (onSignatureInjected) {
        onSignatureInjected(email);
      }
    } catch (err: any) {
      console.error('Błąd wstrzykiwania stopki:', err);
      setInjectionError(
        err.message || 'Wystąpił błąd podczas wstrzykiwania stopki do Gmaila. Sprawdź swoje uprawnienia.'
      );
    } finally {
      setIsInjecting(false);
    }
  };

  // Asset upload to Firebase Storage (/tenants/{orgId}/assets/{uniqueToken}_${fileName})
  const handleUploadAsset = async (file: File, type: 'logo' | 'avatar' | 'banner') => {
    if (!file.type.startsWith('image/')) {
      setFeedbackNotice({ type: 'error', text: 'Wybrany plik musi być grafiką (PNG, JPG, SVG, WebP).' });
      setTimeout(() => setFeedbackNotice(null), 4000);
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setFeedbackNotice({ type: 'error', text: 'Rozmiar pliku nie może przekraczać 5 MB.' });
      setTimeout(() => setFeedbackNotice(null), 4000);
      return;
    }

    if (type === 'logo') setIsUploadingLogo(true);
    if (type === 'banner') setIsUploadingBanner(true);

    let measuredLogoWidth: number | undefined;
    let measuredLogoHeight: number | undefined;

    if (type === 'logo') {
      try {
        const objectUrl = URL.createObjectURL(file);
        const dims = await new Promise<{ width: number; height: number }>((resolve) => {
          const img = new Image();
          img.onload = () => {
            const res = { width: img.naturalWidth, height: img.naturalHeight };
            URL.revokeObjectURL(objectUrl);
            resolve(res);
          };
          img.onerror = () => {
            URL.revokeObjectURL(objectUrl);
            resolve({ width: 240, height: 68 });
          };
          img.src = objectUrl;
        });
        measuredLogoWidth = dims.width;
        measuredLogoHeight = dims.height;
        setLogoWidth(dims.width);
        setLogoHeight(dims.height);
      } catch (dimErr) {
        console.warn('Nie udało się odczytać wymiarów pliku logo:', dimErr);
      }
    }

    try {
      const downloadUrl = await uploadAsset(file, domain);
      if (type === 'logo') {
        setLogoUrl(downloadUrl);
        if (measuredLogoWidth && measuredLogoHeight) {
          setLogoWidth(measuredLogoWidth);
          setLogoHeight(measuredLogoHeight);
        }
        setToggles((prev) => ({ ...prev, showCompany: true }));
      }
      if (type === 'banner') {
        setBannerUrl(downloadUrl);
        setToggles((prev) => ({ ...prev, showBanner: true }));
      }

      setIsDirty(true);
      setFeedbackNotice({
        type: 'success',
        text: `Grafikę "${file.name}" pomyślnie wgrano do Firebase Storage (${type})!`,
      });
    } catch (error: any) {
      console.warn('Firebase Storage upload warning, using local preview:', error);
      const reader = new FileReader();
      reader.onload = (ev) => {
        const dataUrl = ev.target?.result as string;
        if (dataUrl) {
          if (type === 'logo') {
            setLogoUrl(dataUrl);
            if (measuredLogoWidth && measuredLogoHeight) {
              setLogoWidth(measuredLogoWidth);
              setLogoHeight(measuredLogoHeight);
            }
            setToggles((prev) => ({ ...prev, showCompany: true }));
          }
          if (type === 'banner') {
            setBannerUrl(dataUrl);
            setToggles((prev) => ({ ...prev, showBanner: true }));
          }
          setIsDirty(true);
          setFeedbackNotice({
            type: 'success',
            text: `Wczytano grafikę lokalnie (${file.name}).`,
          });
        }
      };
      reader.readAsDataURL(file);
    } finally {
      if (type === 'logo') setIsUploadingLogo(false);
      if (type === 'banner') setIsUploadingBanner(false);
      setTimeout(() => setFeedbackNotice(null), 4000);
    }
  };

  // Save current signature configuration to Firestore: organizations/{orgId}/signatures/{signatureId}
  const handleSaveToFirestore = async () => {
    setIsSaving(true);
    try {
      const currentTplName = savedTemplates.find((t) => t.id === activeSavedTemplateId)?.name || 'Szablon Domyślny';
      const templateToSave: Partial<SavedSignatureTemplate> = {
        name: currentTplName,
        templateId,
        primaryColor,
        toggles,
        socials: socialItems,
        companyName,
        companyPhone: phone || undefined,
        websiteUrl: websiteUrl || undefined,
        nip: nip || undefined,
        regon: regon || undefined,
        address: address || undefined,
        logoUrl: logoUrl || undefined,
        logoWidth: logoWidth || undefined,
        logoHeight: logoHeight || undefined,
        bannerUrl: bannerUrl || undefined,
        bannerLink: bannerLink || undefined,
        bannerAlt: bannerAlt || undefined,
        disclaimer: disclaimer || undefined,
        promoTitle: promoTitle || undefined,
        promoText: promoText || undefined,
        promoCtaText: promoCtaText || undefined,
      };

      const saved = await saveSignatureToFirestore(domain, activeSavedTemplateId, templateToSave);
      setIsDirty(false);
      setLastSavedAt(new Date());
      setFeedbackNotice({
        type: 'success',
        text: `Zapisano szablon "${saved.name}" w Firestore (organizations/${domain}/signatures/${activeSavedTemplateId})!`,
      });
      if (onSaveTemplate) {
        onSaveTemplate(saved);
      }
    } catch (err: any) {
      console.error('Błąd zapisu w Firestore:', err);
      setFeedbackNotice({
        type: 'error',
        text: `Błąd zapisu w Firestore: ${err.message}`,
      });
    } finally {
      setIsSaving(false);
      setTimeout(() => setFeedbackNotice(null), 4000);
    }
  };

  // Template management actions: cleanly switch templates without cross-template pollution
  const handleLoadTemplate = async (tplId: string) => {
    // 1. Snapshot and save current active template so its unique modifications are preserved
    if (isDirty) {
      const currentTplName = savedTemplates.find((t) => t.id === activeSavedTemplateId)?.name || 'Szablon';
      const currentSnapshot: SavedSignatureTemplate = {
        id: activeSavedTemplateId,
        name: currentTplName,
        templateId,
        primaryColor,
        toggles,
        companyName,
        companyPhone: phone || undefined,
        websiteUrl: websiteUrl || undefined,
        nip: nip || undefined,
        regon: regon || undefined,
        address: address || undefined,
        logoUrl: logoUrl || undefined,
        logoWidth: logoWidth || undefined,
        logoHeight: logoHeight || undefined,
        bannerUrl: bannerUrl || undefined,
        bannerLink: bannerLink || undefined,
        bannerAlt: bannerAlt || undefined,
        disclaimer: disclaimer || undefined,
        promoTitle: promoTitle || undefined,
        promoText: promoText || undefined,
        promoCtaText: promoCtaText || undefined,
        socials: socialItems,
        updatedAt: new Date().toISOString(),
      };
      saveSignatureToFirestore(domain, activeSavedTemplateId, currentSnapshot).catch(console.warn);
      if (onSaveTemplate) {
        onSaveTemplate(currentSnapshot);
      }
    }

    // 2. Switch active ID and cleanly apply target template's fields
    setActiveSavedTemplateId(tplId);
    let targetTemplate: SavedSignatureTemplate | null | undefined = savedTemplates.find((t) => t.id === tplId);
    if (!targetTemplate) {
      targetTemplate = await loadSignatureFromFirestore(domain, tplId);
    }
    if (targetTemplate) {
      applyTemplateToState(targetTemplate);
      setIsDirty(false);
      setLastSavedAt(targetTemplate.updatedAt ? new Date(targetTemplate.updatedAt) : new Date());
      setFeedbackNotice({
        type: 'success',
        text: `Wczytano szablon: ${targetTemplate.name}`,
      });
      setTimeout(() => setFeedbackNotice(null), 3500);
    }
  };

  const handleSaveAsTemplate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!templateNameInput.trim()) return;

    const newId = `tpl_${Date.now()}`;
    const payload: Partial<SavedSignatureTemplate> = {
      id: newId,
      name: templateNameInput.trim(),
      templateId,
      primaryColor,
      toggles,
      socials: socialItems,
      companyName,
      companyPhone: phone || undefined,
      websiteUrl: websiteUrl || undefined,
      nip: nip || undefined,
      regon: regon || undefined,
      address: address || undefined,
      logoUrl: logoUrl || undefined,
      logoWidth: logoWidth || undefined,
      logoHeight: logoHeight || undefined,
      bannerUrl: bannerUrl || undefined,
      bannerLink: bannerLink || undefined,
      bannerAlt: bannerAlt || undefined,
      disclaimer: disclaimer || undefined,
      promoTitle: promoTitle || undefined,
      promoText: promoText || undefined,
      promoCtaText: promoCtaText || undefined,
    };

    const saved = await saveSignatureToFirestore(domain, newId, payload);
    if (onSaveTemplate) {
      onSaveTemplate(saved);
    }

    setIsSaveModalOpen(false);
    setTemplateNameInput('');
    setActiveSavedTemplateId(saved.id);
    setIsDirty(false);
    setLastSavedAt(new Date());
    setFeedbackNotice({
      type: 'success',
      text: `Szablon "${saved.name}" został pomyślnie zapisany w Firestore!`,
    });
    setTimeout(() => setFeedbackNotice(null), 4000);
  };

  const handleConfirmApply = async () => {
    const currentTplId = activeSavedTemplateId || savedTemplates[0]?.id || 'default';
    const currentTplName = savedTemplates.find((t) => t.id === currentTplId)?.name || 'Szablon Domyślny';
    const safeLogo = (logoUrl && !logoUrl.startsWith('data:image/svg')) ? logoUrl : DEFAULT_CEREBRO_LOGO;

    // Construct full template configuration reflecting all current customizations
    const currentTemplateConfig: SavedSignatureTemplate = {
      id: currentTplId,
      name: currentTplName,
      templateId,
      primaryColor,
      toggles,
      companyName,
      websiteUrl,
      nip,
      regon,
      address,
      logoUrl: safeLogo,
      logoWidth: logoWidth || undefined,
      logoHeight: logoHeight || undefined,
      bannerUrl: bannerUrl || undefined,
      bannerLink,
      bannerAlt,
      promoTitle,
      promoText,
      promoCtaText,
      disclaimer,
      socials: socialItems,
      updatedAt: new Date().toISOString(),
    };

    setIsSaving(true);
    try {
      // Save and sync template to Firestore so all views immediately reflect it
      await saveSignatureToFirestore(domain, currentTplId, currentTemplateConfig);
      if (onSaveTemplate) {
        onSaveTemplate(currentTemplateConfig);
      }

      if (selectedApplyTarget === 'group') {
        if (onApplyTemplateToGroup) {
          await onApplyTemplateToGroup(selectedApplyGroupId, currentTplId);
        }
        const groupName = groups.find((g) => g.id === selectedApplyGroupId)?.name || 'grupy';
        setFeedbackNotice({
          type: 'success',
          text: `Szablon "${currentTplName}" został przypisany do grupy "${groupName}"!`,
        });
      } else if (selectedApplyTarget === 'all') {
        if (onApplyTemplateToAll) {
          await onApplyTemplateToAll(currentTplId);
        }
        setFeedbackNotice({
          type: 'success',
          text: `Szablon "${currentTplName}" został pomyślnie przypisany do WSZYSTKICH pracowników w domenie!`,
        });
      } else if (selectedApplyTarget === 'user' && activeUser) {
        if (onApplyTemplateToUser) {
          await onApplyTemplateToUser(activeUser.id, currentTplId);
        }
        setFeedbackNotice({
          type: 'success',
          text: `Szablon "${currentTplName}" został przypisany do pracownika ${activeUser.fullName}!`,
        });
      }
    } catch (err: any) {
      console.error('Błąd zapisu lub przypisywania szablonu:', err);
      setFeedbackNotice({
        type: 'error',
        text: `Wystąpił błąd podczas przypisywania: ${err.message}`,
      });
    } finally {
      setIsSaving(false);
      setIsApplyModalOpen(false);
      setTimeout(() => setFeedbackNotice(null), 4000);
    }
  };

  return (
    <div className="editor-grid">
      {/* LEFT COLUMN: VISUAL CONTROLS */}
      <div className="card" style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 18 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #e2e8f0', paddingBottom: 12, flexWrap: 'wrap', gap: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 34, height: 34, borderRadius: 8, background: 'rgba(2, 132, 199, 0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#0066FF' }}>
              <Layout size={18} />
            </div>
            <div>
              <h2 style={{ fontSize: 16, fontWeight: 700, color: '#0B192C', margin: 0 }}>Kreator Stopki</h2>
              <div style={{ fontSize: 11, color: '#475569' }}>Dostosuj szablony, pola i kolory na żywo</div>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            {/* Status indicator: Niezapisane zmiany / Zapisano */}
            {isLoadingTemplate ? (
              <span
                className="badge badge-neutral"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 5,
                  padding: '5px 10px',
                  fontWeight: 600,
                  fontSize: 11,
                }}
                title="Pobieranie konfiguracji stopki z kolekcji Firestore organizations/{orgId}/signatures/{signatureId}"
              >
                <Loader2 size={12} className="animate-spin" />
                Wczytywanie z Firestore...
              </span>
            ) : isDirty ? (
              <span
                className="badge badge-warning"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '5px 10px',
                  fontWeight: 600,
                  fontSize: 11,
                  boxShadow: '0 0 10px rgba(245, 158, 11, 0.2)',
                }}
                title="Wprowadzono zmiany w konfiguracji stopki, które oczekują na zapisanie"
              >
                <span
                  style={{
                    width: 7,
                    height: 7,
                    borderRadius: '50%',
                    backgroundColor: '#f59e0b',
                    display: 'inline-block',
                  }}
                />
                Niezapisane zmiany
              </span>
            ) : (
              <span
                className="badge badge-success"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 5,
                  padding: '5px 10px',
                  fontWeight: 600,
                  fontSize: 11,
                }}
                title={lastSavedAt ? `Zapisano w Firestore o ${lastSavedAt.toLocaleTimeString()}` : 'Wszystkie zmiany są zapisane w Firestore'}
              >
                <CheckCircle2 size={13} />
                Zapisano {lastSavedAt ? `(${lastSavedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })})` : ''}
              </span>
            )}

            {/* WYRAZISTY PRZYCISK "ZAPISZ SZABLON" */}
            <button
              type="button"
              onClick={handleSaveToFirestore}
              disabled={isSaving}
              className="btn btn-primary"
              style={{
                padding: '7px 14px',
                fontSize: 12,
                fontWeight: 700,
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                background: 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)',
                boxShadow: isDirty ? '0 0 14px rgba(2, 132, 199, 0.45)' : undefined,
                border: '1px solid #38bdf8',
                cursor: isSaving ? 'wait' : 'pointer',
              }}
              title="Zapisz obecną konfigurację stopki w kolekcji Firestore: organizations/{orgId}/signatures/{signatureId}"
            >
              {isSaving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              <span>{isSaving ? 'Zapisywanie...' : 'Zapisz szablon'}</span>
            </button>
          </div>
        </div>

        {/* Saved Templates Quick Bar */}
        <div
          style={{
            background: '#f8fafc',
            border: '1px solid #e2e8f0',
            borderRadius: 10,
            padding: 12,
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              Szablony & Zastosowanie
            </span>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={() => setIsSaveModalOpen(true)}
                title="Zapisz obecne ustawienia jako nowy szablon"
                style={{
                  background: 'rgba(2, 132, 199, 0.15)',
                  border: '1px solid rgba(2, 132, 199, 0.3)',
                  color: '#0066FF',
                  borderRadius: 6,
                  padding: '4px 8px',
                  fontSize: 11,
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                }}
              >
                <Bookmark size={12} />
                <span>Zapisz jako szablon</span>
              </button>

              <button
                type="button"
                onClick={() => setIsApplyModalOpen(true)}
                title="Zastosuj ten szablon do użytkowników lub grup"
                style={{
                  background: 'rgba(16, 185, 129, 0.15)',
                  border: '1px solid rgba(16, 185, 129, 0.3)',
                  color: '#34d399',
                  borderRadius: 6,
                  padding: '4px 8px',
                  fontSize: 11,
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                }}
              >
                <Layers size={12} />
                <span>Zastosuj do...</span>
              </button>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <select
              value={activeSavedTemplateId}
              onChange={(e) => handleLoadTemplate(e.target.value)}
              style={{
                width: '100%',
                background: '#f1f5f9',
                border: '1px solid #e2e8f0',
                borderRadius: 8,
                padding: '7px 10px',
                color: '#0B192C',
                fontSize: 12,
                cursor: 'pointer',
              }}
            >
              {savedTemplates.map((tpl) => (
                <option key={tpl.id} value={tpl.id}>
                  {tpl.name}
                </option>
              ))}
            </select>
          </div>

          {feedbackNotice && (
            <div
              style={{
                padding: '6px 10px',
                borderRadius: 6,
                fontSize: 11,
                background: feedbackNotice.type === 'success' ? 'rgba(16, 185, 129, 0.15)' : 'rgba(239, 68, 68, 0.15)',
                border: `1px solid ${feedbackNotice.type === 'success' ? '#10b981' : '#ef4444'}`,
                color: feedbackNotice.type === 'success' ? '#6ee7b7' : '#fca5a5',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              <CheckCircle2 size={13} />
              <span>{feedbackNotice.text}</span>
            </div>
          )}
        </div>

        {/* Editor Sub-tabs */}
        <div className="editor-subtabs">
          <button
            onClick={() => setEditorTab('template')}
            className="btn"
            style={{
              padding: '6px 4px',
              fontSize: 11,
              background: editorTab === 'template' ? '#0066FF' : 'transparent',
              color: editorTab === 'template' ? '#fff' : '#475569',
              borderRadius: 6,
              justifyContent: 'center',
            }}
          >
            <Layout size={13} />
            <span>Szablon</span>
          </button>

          <button
            onClick={() => setEditorTab('fields')}
            className="btn"
            style={{
              padding: '6px 4px',
              fontSize: 11,
              background: editorTab === 'fields' ? '#0066FF' : 'transparent',
              color: editorTab === 'fields' ? '#fff' : '#475569',
              borderRadius: 6,
              justifyContent: 'center',
            }}
            title="Dane Firmy & Logo w stopce"
          >
            <Building size={13} />
            <span>Firma & Logo</span>
          </button>

          <button
            onClick={() => setEditorTab('contact')}
            className="btn"
            style={{
              padding: '6px 4px',
              fontSize: 11,
              background: editorTab === 'contact' ? '#0066FF' : 'transparent',
              color: editorTab === 'contact' ? '#fff' : '#475569',
              borderRadius: 6,
              justifyContent: 'center',
            }}
          >
            <Phone size={13} />
            <span>Kontakt</span>
          </button>

          <button
            onClick={() => setEditorTab('socials')}
            className="btn"
            style={{
              padding: '6px 4px',
              fontSize: 11,
              background: editorTab === 'socials' ? '#0066FF' : 'transparent',
              color: editorTab === 'socials' ? '#fff' : '#475569',
              borderRadius: 6,
              justifyContent: 'center',
            }}
          >
            <Share2 size={13} />
            <span>Sociale</span>
          </button>

          <button
            onClick={() => setEditorTab('banner')}
            className="btn"
            style={{
              padding: '6px 4px',
              fontSize: 11,
              background: editorTab === 'banner' ? '#0066FF' : 'transparent',
              color: editorTab === 'banner' ? '#fff' : '#475569',
              borderRadius: 6,
              justifyContent: 'center',
            }}
          >
            <Megaphone size={13} />
            <span>Baner</span>
          </button>

          <button
            onClick={() => setEditorTab('legal')}
            className="btn"
            style={{
              padding: '6px 4px',
              fontSize: 11,
              background: editorTab === 'legal' ? '#0066FF' : 'transparent',
              color: editorTab === 'legal' ? '#fff' : '#475569',
              borderRadius: 6,
              justifyContent: 'center',
            }}
          >
            <FileText size={13} />
            <span>RODO</span>
          </button>
        </div>

        {/* TAB 1: SZABLON & KOLORY */}
        {editorTab === 'template' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 700, color: '#0B192C', display: 'block', marginBottom: 8 }}>
                Wybierz Szablon HTML:
              </label>
              <div className="modal-grid-2">
                {[
                  { id: 'modern_horizontal', name: 'Modern Horizontal', desc: 'Awatar + separator + logo' },
                  { id: 'clean_minimal', name: 'Clean Minimal', desc: 'Bez foto, minimalistyczna elegancja' },
                  { id: 'corporate_compact', name: 'Corporate Compact', desc: 'Niska stopka (<65px) na wątki' },
                  { id: 'marketing_focus', name: 'Marketing Focus', desc: 'Wyróżniony baner i przycisk CTA' },
                ].map((tpl) => (
                  <button
                    key={tpl.id}
                    onClick={() => setTemplateId(tpl.id as TemplateId)}
                    style={{
                      background: templateId === tpl.id ? 'rgba(0, 102, 255, 0.08)' : '#ffffff',
                      border: `1.5px solid ${templateId === tpl.id ? '#0066FF' : '#cbd5e1'}`,
                      borderRadius: 8,
                      padding: '12px 14px',
                      textAlign: 'left',
                      cursor: 'pointer',
                      transition: 'all 0.15s ease',
                      boxShadow: templateId === tpl.id ? '0 2px 8px rgba(0, 102, 255, 0.15)' : 'none',
                    }}
                  >
                    <div style={{ fontSize: 13, fontWeight: 700, color: templateId === tpl.id ? '#0066FF' : '#0B192C' }}>
                      {tpl.name}
                    </div>
                    <div style={{ fontSize: 11, color: '#475569', marginTop: 4 }}>
                      {tpl.desc}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label style={{ fontSize: 12, fontWeight: 700, color: '#0B192C', display: 'block', marginBottom: 8 }}>
                Kolor Wiodący (Brand Accent):
              </label>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                {COLOR_PALETTE.map((c) => (
                  <button
                    key={c.value}
                    onClick={() => setPrimaryColor(c.value)}
                    style={{
                      width: 28,
                      height: 28,
                      borderRadius: '50%',
                      background: c.value,
                      border: primaryColor === c.value ? '2px solid #0066FF' : '1px solid #cbd5e1',
                      cursor: 'pointer',
                      boxShadow: primaryColor === c.value ? '0 0 0 2px rgba(0, 102, 255, 0.3)' : 'none',
                    }}
                    title={c.name}
                  />
                ))}
                <input
                  type="color"
                  value={primaryColor}
                  onChange={(e) => setPrimaryColor(e.target.value)}
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: 6,
                    background: 'transparent',
                    border: '1px solid #e2e8f0',
                    cursor: 'pointer',
                  }}
                  title="Wybierz własny kolor"
                />
              </div>
            </div>
          </div>
        )}

        {/* TAB 2: DANE, AWATAR & LOGO FIRMY */}
        {editorTab === 'fields' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* Logo Section */}
            <div style={{ background: '#f8fafc', padding: 12, borderRadius: 8, border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: '#0B192C' }}>Logo Firmowe w stopce</span>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#475569', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={toggles.showCompany}
                    onChange={(e) => {
                      setToggles({ ...toggles, showCompany: e.target.checked });
                      setIsDirty(true);
                    }}
                  />
                  <span>Pokazuj w stopce</span>
                </label>
              </div>

              {toggles.showCompany && (
                <ImageUploadCard
                  label="Grafika Logo (PNG, SVG, JPG)"
                  description="Wgraj własne logo firmy do Firebase Storage lub zachowaj domyślne logo CerebroLabs."
                  currentUrl={logoUrl !== DEFAULT_CEREBRO_LOGO ? logoUrl : ''}
                  fallbackUrl={DEFAULT_CEREBRO_LOGO}
                  isUploading={isUploadingLogo}
                  onFileSelect={(file) => handleUploadAsset(file, 'logo')}
                  onClear={() => {
                    setLogoUrl(DEFAULT_CEREBRO_LOGO);
                    setLogoWidth(240);
                    setLogoHeight(68);
                    setIsDirty(true);
                  }}
                  clearButtonText="Przywróć domyślne logo"
                  previewShape="rounded"
                />
              )}
            </div>

            {/* Avatar Section - Template Level */}
            <div style={{ background: '#f8fafc', padding: 14, borderRadius: 8, border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                <div>
                  <span style={{ fontSize: 13, fontWeight: 700, color: '#0B192C' }}>Awatary pracowników w stopce</span>
                  <div style={{ fontSize: 11, color: '#64748b' }}>Czy ten szablon ma wyświetlać zdjęcia twarzy pracowników: TAK/NIE</div>
                </div>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: toggles.showAvatar ? '#38bdf8' : '#94a3b8', cursor: 'pointer', fontWeight: 600 }}>
                  <input
                    type="checkbox"
                    className="custom-checkbox"
                    checked={toggles.showAvatar}
                    onChange={(e) => {
                      setToggles({ ...toggles, showAvatar: e.target.checked });
                      setIsDirty(true);
                    }}
                  />
                  <span>{toggles.showAvatar ? 'WŁĄCZONE' : 'WYŁĄCZONE'}</span>
                </label>
              </div>

              {toggles.showAvatar && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10, borderTop: '1px solid #e2e8f0', paddingTop: 10 }}>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 11, color: '#475569' }}>Kształt awatara w szablonie:</span>
                    {(['circle', 'rounded', 'square'] as const).map((sh) => (
                      <button
                        key={sh}
                        type="button"
                        onClick={() => {
                          setToggles({ ...toggles, avatarShape: sh });
                          setIsDirty(true);
                        }}
                        style={{
                          padding: '4px 9px',
                          fontSize: 10.5,
                          borderRadius: 5,
                          background: toggles.avatarShape === sh ? '#0066FF' : '#ffffff',
                          color: toggles.avatarShape === sh ? '#ffffff' : '#334155',
                          border: toggles.avatarShape === sh ? '1px solid #0066FF' : '1px solid #cbd5e1',
                          cursor: 'pointer',
                          fontWeight: toggles.avatarShape === sh ? 700 : 500,
                        }}
                      >
                        {sh === 'circle' ? 'Koło' : sh === 'rounded' ? 'Zaokrąglony' : 'Kwadrat'}
                      </button>
                    ))}
                  </div>

                  <div
                    style={{
                      background: 'rgba(0, 102, 255, 0.05)',
                      border: '1px solid rgba(0, 102, 255, 0.18)',
                      borderRadius: 6,
                      padding: '10px 12px',
                      fontSize: 11,
                      color: '#334155',
                      lineHeight: 1.5,
                    }}
                  >
                    <strong style={{ color: '#0066FF' }}>Hierarchia danych:</strong> Zdjęcie profilowe konkretnego pracownika wgrywa się w zakładce <strong>Lista Pracowników → Edytuj</strong> do Firebase Storage. W tym szablonie konfigurujesz jedynie, czy stopka uwzględnia kolumnę awatara oraz jej kształt geometryczny.
                  </div>
                </div>
              )}
            </div>

            {/* Informacja o danych personalnych */}
            <div
              style={{
                background: 'rgba(56, 189, 248, 0.08)',
                border: '1px solid rgba(56, 189, 248, 0.25)',
                borderRadius: 8,
                padding: '12px 14px',
                display: 'flex',
                flexDirection: 'column',
                gap: 6,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#0066FF', fontSize: 12, fontWeight: 700 }}>
                <Users size={14} />
                <span>Dane personalne pracowników</span>
              </div>
              <p style={{ margin: 0, fontSize: 11.5, color: '#334155', lineHeight: 1.5 }}>
                Pola personalne pracowników (<strong>Imię i Nazwisko</strong>, <strong>Stanowisko</strong> oraz <strong>Dział / Departament</strong>) są zarządzane z poziomu profilu pracownika w zakładce <strong>Użytkownicy domeny</strong>. W tym edytorze definiujesz globalną nazwę firmy, logo oraz wygląd szablonu.
              </p>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 4 }}>
                <span style={{ fontSize: 10, background: '#eff6ff', border: '1px solid #bfdbfe', padding: '2px 7px', borderRadius: 4, color: '#1d4ed8', fontWeight: 600 }}>
                  {'{Imię i Nazwisko}'}
                </span>
                <span style={{ fontSize: 10, background: '#eff6ff', border: '1px solid #bfdbfe', padding: '2px 7px', borderRadius: 4, color: '#1d4ed8', fontWeight: 600 }}>
                  {'{Stanowisko}'}
                </span>
                <span style={{ fontSize: 10, background: '#eff6ff', border: '1px solid #bfdbfe', padding: '2px 7px', borderRadius: 4, color: '#1d4ed8', fontWeight: 600 }}>
                  {'{Dział / Departament}'}
                </span>
              </div>
            </div>

            {/* Nazwa Firmy */}
            <div>
              <label style={{ fontSize: 11, color: '#475569', display: 'block', marginBottom: 4 }}>
                Nazwa Firmy w stopce
              </label>
              <input
                type="text"
                value={companyName}
                onChange={(e) => {
                  setCompanyName(e.target.value);
                  setIsDirty(true);
                }}
                className="input-control"
                style={{ fontSize: 12, padding: '7px 10px' }}
                placeholder="np. CerebroLabs Sp. z o.o."
              />
            </div>
          </div>
        )}

        {/* TAB 3: KONTAKT */}
        {editorTab === 'contact' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div>
              <label style={{ fontSize: 11, color: '#475569', display: 'block', marginBottom: 4 }}>Telefon służbowy</label>
              <input
                type="text"
                value={phone}
                onChange={(e) => {
                  setPhone(e.target.value);
                  setIsDirty(true);
                }}
                className="input-control"
                style={{ fontSize: 12, padding: '7px 10px' }}
              />
            </div>

            <div>
              <label style={{ fontSize: 11, color: '#475569', display: 'block', marginBottom: 4 }}>Adres Email</label>
              <input
                type="text"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setIsDirty(true);
                }}
                className="input-control"
                style={{ fontSize: 12, padding: '7px 10px' }}
              />
            </div>

            <div>
              <label style={{ fontSize: 11, color: '#475569', display: 'block', marginBottom: 4 }}>Strona WWW</label>
              <input
                type="text"
                value={websiteUrl}
                onChange={(e) => {
                  setWebsiteUrl(e.target.value);
                  setIsDirty(true);
                }}
                className="input-control"
                style={{ fontSize: 12, padding: '7px 10px' }}
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div>
                <label style={{ fontSize: 11, color: '#475569', display: 'block', marginBottom: 4 }}>NIP</label>
                <input
                  type="text"
                  value={nip}
                  onChange={(e) => {
                    setNip(e.target.value);
                    setIsDirty(true);
                  }}
                  className="input-control"
                  style={{ fontSize: 12, padding: '7px 10px' }}
                />
              </div>
              <div>
                <label style={{ fontSize: 11, color: '#475569', display: 'block', marginBottom: 4 }}>REGON</label>
                <input
                  type="text"
                  value={regon}
                  onChange={(e) => {
                    setRegon(e.target.value);
                    setIsDirty(true);
                  }}
                  className="input-control"
                  style={{ fontSize: 12, padding: '7px 10px' }}
                />
              </div>
            </div>

            <div>
              <label style={{ fontSize: 11, color: '#475569', display: 'block', marginBottom: 4 }}>Adres siedziby</label>
              <input
                type="text"
                value={address}
                onChange={(e) => {
                  setAddress(e.target.value);
                  setIsDirty(true);
                }}
                className="input-control"
                style={{ fontSize: 12, padding: '7px 10px' }}
              />
            </div>
          </div>
        )}

        {/* TAB 4: SOCIAL MEDIA (WARUNKOWE RENDEROWANIE & OBIEKTY) */}
        {editorTab === 'socials' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <span style={{ fontSize: 12, fontWeight: 700, color: '#0B192C' }}>Ikony Społecznościowe</span>
                <div style={{ fontSize: 10, color: '#475569' }}>
                  Wyłączone ikony lub ikony z pustym adresem URL nie pojawią się w kodzie HTML stopki.
                </div>
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#475569', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={toggles.showSocials}
                  onChange={(e) => {
                    setToggles({ ...toggles, showSocials: e.target.checked });
                    setIsDirty(true);
                  }}
                />
                <span>Włączone</span>
              </label>
            </div>

            {toggles.showSocials && (
              <>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {socialItems.map((item, idx) => (
                    <div
                      key={item.platform}
                      style={{
                        background: item.enabled ? '#ffffff' : '#f8fafc',
                        border: item.enabled ? '1px solid #0066FF' : '1px solid #e2e8f0',
                        borderRadius: 8,
                        padding: 10,
                        opacity: item.enabled ? 1 : 0.7,
                        transition: 'all 0.15s ease',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 8,
                        boxShadow: item.enabled ? '0 1px 4px rgba(0, 102, 255, 0.08)' : 'none',
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={item.enabled}
                            onChange={(e) => {
                              const updated = [...socialItems];
                              updated[idx] = { ...updated[idx], enabled: e.target.checked };
                              setSocialItems(updated);
                              setIsDirty(true);
                            }}
                          />
                          <span
                            style={{
                              fontSize: 12.5,
                              fontWeight: 600,
                              color: item.enabled ? '#0B192C' : '#64748b',
                              textTransform: 'capitalize',
                            }}
                          >
                            {item.platform === 'twitter' ? 'X / Twitter' : item.platform}
                          </span>
                        </label>

                        <button
                          type="button"
                          onClick={() => {
                            const filtered = socialItems.filter((_, i) => i !== idx);
                            setSocialItems(filtered);
                            setIsDirty(true);
                          }}
                          className="btn"
                          title={`Usuń platformę ${item.platform}`}
                          style={{
                            padding: '3px 8px',
                            fontSize: 10,
                            background: 'rgba(239, 68, 68, 0.12)',
                            color: '#f87171',
                            border: '1px solid rgba(239, 68, 68, 0.25)',
                            borderRadius: 5,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 4,
                          }}
                        >
                          <Trash2 size={11} />
                          <span>Usuń</span>
                        </button>
                      </div>

                      {item.enabled && (
                        <input
                          type="text"
                          placeholder={`https://${item.platform}.com/...`}
                          value={item.url}
                          onChange={(e) => {
                            const updated = [...socialItems];
                            updated[idx] = { ...updated[idx], url: e.target.value };
                            setSocialItems(updated);
                            setIsDirty(true);
                          }}
                          className="input-control"
                          style={{ fontSize: 11, padding: '6px 10px' }}
                        />
                      )}
                    </div>
                  ))}
                </div>

                {/* Add Platform dropdown if any are missing */}
                {AVAILABLE_SOCIAL_PLATFORMS.filter(
                  (p) => !socialItems.some((item) => item.platform.toLowerCase() === p)
                ).length > 0 && (
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 4 }}>
                    <select
                      id="add-platform-select"
                      className="input-control"
                      style={{ fontSize: 11, padding: '6px 10px', width: 'auto', cursor: 'pointer' }}
                    >
                      {AVAILABLE_SOCIAL_PLATFORMS.filter(
                        (p) => !socialItems.some((item) => item.platform.toLowerCase() === p)
                      ).map((p) => (
                        <option key={p} value={p}>
                          {p === 'twitter' ? 'X / Twitter' : p}
                        </option>
                      ))}
                    </select>

                    <button
                      type="button"
                      onClick={() => {
                        const sel = document.getElementById('add-platform-select') as HTMLSelectElement;
                        if (sel && sel.value) {
                          setSocialItems([
                            ...socialItems,
                            { platform: sel.value, url: '', enabled: true },
                          ]);
                          setIsDirty(true);
                        }
                      }}
                      className="btn btn-secondary"
                      style={{ padding: '6px 12px', fontSize: 11, display: 'flex', alignItems: 'center', gap: 4 }}
                    >
                      <Plus size={12} />
                      <span>Dodaj ikonę</span>
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* TAB 5: BANER MARKETINGOWY & HOSTING STORAGE */}
        {editorTab === 'banner' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: '#0B192C' }}>Baner promocyjny</span>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#475569', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={toggles.showBanner}
                  onChange={(e) => {
                    setToggles({ ...toggles, showBanner: e.target.checked });
                    setIsDirty(true);
                  }}
                />
                <span>Włączony</span>
              </label>
            </div>

            {toggles.showBanner && (
              <>
                {/* Banner Upload with Firebase Storage */}
                <ImageUploadCard
                  label="Grafika Banera"
                  description="Wgraj baner do Firebase Storage (np. 488x60px lub 520x90px)."
                  currentUrl={bannerUrl}
                  isUploading={isUploadingBanner}
                  onFileSelect={(file) => handleUploadAsset(file, 'banner')}
                  onClear={() => {
                    setBannerUrl('');
                    setIsDirty(true);
                  }}
                  clearButtonText="Usuń baner"
                  previewShape="banner"
                />

                <div>
                  <label style={{ fontSize: 11, color: '#475569', display: 'block', marginBottom: 4 }}>
                    Nagłówek Boksu Promocyjnego:
                  </label>
                  <input
                    type="text"
                    value={promoTitle}
                    onChange={(e) => {
                      setPromoTitle(e.target.value);
                      setIsDirty(true);
                    }}
                    className="input-control"
                    style={{ fontSize: 12, padding: '7px 10px' }}
                  />
                </div>

                <div>
                  <label style={{ fontSize: 11, color: '#475569', display: 'block', marginBottom: 4 }}>
                    Tekst promocyjny:
                  </label>
                  <input
                    type="text"
                    value={promoText}
                    onChange={(e) => {
                      setPromoText(e.target.value);
                      setIsDirty(true);
                    }}
                    className="input-control"
                    style={{ fontSize: 12, padding: '7px 10px' }}
                  />
                </div>

                <div>
                  <label style={{ fontSize: 11, color: '#475569', display: 'block', marginBottom: 4 }}>
                    Tekst Przycisku CTA:
                  </label>
                  <input
                    type="text"
                    value={promoCtaText}
                    onChange={(e) => {
                      setPromoCtaText(e.target.value);
                      setIsDirty(true);
                    }}
                    className="input-control"
                    style={{ fontSize: 12, padding: '7px 10px' }}
                  />
                </div>

                <div>
                  <label style={{ fontSize: 11, color: '#475569', display: 'block', marginBottom: 4 }}>
                    Docelowy URL z tagami UTM:
                  </label>
                  <input
                    type="text"
                    value={bannerLink}
                    onChange={(e) => {
                      setBannerLink(e.target.value);
                      setIsDirty(true);
                    }}
                    className="input-control"
                    style={{ fontSize: 12, padding: '7px 10px' }}
                  />
                </div>
              </>
            )}
          </div>
        )}

        {/* TAB 6: RODO & NOTKA PRAWNA */}
        {editorTab === 'legal' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: '#0B192C' }}>Notka prawna i RODO</span>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#475569', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={toggles.showDisclaimer}
                  onChange={(e) => setToggles({ ...toggles, showDisclaimer: e.target.checked })}
                />
                <span>Włączona</span>
              </label>
            </div>

            {toggles.showDisclaimer && (
              <div>
                <label style={{ fontSize: 11, color: '#475569', display: 'block', marginBottom: 4 }}>
                  Treść klauzuli poufności / RODO:
                </label>
                <textarea
                  value={disclaimer}
                  onChange={(e) => setDisclaimer(e.target.value)}
                  className="input-control"
                  rows={4}
                  style={{ fontSize: 11, padding: '8px 10px', resize: 'vertical' }}
                />
              </div>
            )}
          </div>
        )}
      </div>

      {/* RIGHT COLUMN: LIVE PREVIEW & GMAIL INJECTION */}
      <div className="sticky-preview-col" style={{ display: 'flex', flexDirection: 'column', gap: 16, position: 'sticky', top: 88, minWidth: 0, maxWidth: '100%' }}>
        <div className="card" style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 16, minWidth: 0, maxWidth: '100%', boxSizing: 'border-box' }}>
          {/* Header & Controls */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Eye size={18} color="#38bdf8" />
              <h3 style={{ fontSize: 16, fontWeight: 700, color: '#0B192C' }}>Podgląd na żywo (520px)</h3>
            </div>

            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', background: '#f8fafc', borderRadius: 8, padding: 2, border: '1px solid #e2e8f0' }}>
                <button
                  onClick={() => setPreviewMode('visual')}
                  className="btn"
                  style={{
                    padding: '4px 10px',
                    fontSize: 12,
                    background: previewMode === 'visual' ? '#0284c7' : 'transparent',
                    color: previewMode === 'visual' ? '#ffffff' : '#94a3b8',
                    borderRadius: 6,
                  }}
                >
                  <Eye size={13} />
                  <span>Wizualny</span>
                </button>
                <button
                  onClick={() => setPreviewMode('code')}
                  className="btn"
                  style={{
                    padding: '4px 10px',
                    fontSize: 12,
                    background: previewMode === 'code' ? '#0284c7' : 'transparent',
                    color: previewMode === 'code' ? '#ffffff' : '#94a3b8',
                    borderRadius: 6,
                  }}
                >
                  <Code size={13} />
                  <span>Kod HTML</span>
                </button>
              </div>

              <button
                type="button"
                onClick={handleSaveToFirestore}
                disabled={isSaving}
                className="btn btn-primary"
                style={{
                  padding: '6px 12px',
                  fontSize: 12,
                  fontWeight: 700,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 5,
                  background: isDirty ? 'linear-gradient(135deg, #0066FF 0%, #0052cc 100%)' : '#f1f5f9',
                  border: isDirty ? '1px solid #0066FF' : '1px solid #e2e8f0',
                  color: isDirty ? '#ffffff' : '#64748b',
                }}
                title="Zapisz konfigurację stopki w Firestore: organizations/{orgId}/signatures/{signatureId}"
              >
                {isSaving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
                <span>{isSaving ? 'Zapisywanie...' : isDirty ? 'Zapisz szablon' : 'Zapisano'}</span>
              </button>

              <button onClick={handleCopyHtml} className="btn btn-outline" style={{ padding: '6px 12px', fontSize: 12 }}>
                {copied ? <Check size={14} color="#10b981" /> : <Copy size={14} />}
                <span>{copied ? 'Skopiowano!' : 'Kopiuj HTML'}</span>
              </button>

              <button
                type="button"
                onClick={handleDownloadHtml}
                className="btn btn-outline"
                style={{ padding: '6px 12px', fontSize: 12 }}
                title="Pobierz plik .html z poprawnym kodowaniem UTF-8"
              >
                <Download size={14} />
                <span>Pobierz HTML</span>
              </button>
            </div>
          </div>

          {/* Feedback alerts */}
          {injectionSuccess && (
            <div
              style={{
                background: 'rgba(16, 185, 129, 0.12)',
                border: '1px solid rgba(16, 185, 129, 0.35)',
                borderRadius: 8,
                padding: '10px 14px',
                fontSize: 13,
                color: '#6ee7b7',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}
            >
              <CheckCircle2 size={16} style={{ flexShrink: 0 }} />
              <span>{injectionSuccess}</span>
            </div>
          )}

          {injectionError && (
            <div
              style={{
                background: 'rgba(239, 68, 68, 0.12)',
                border: '1px solid rgba(239, 68, 68, 0.35)',
                borderRadius: 8,
                padding: '10px 14px',
                fontSize: 13,
                color: '#f87171',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}
            >
              <AlertCircle size={16} style={{ flexShrink: 0 }} />
              <span>{injectionError}</span>
            </div>
          )}

          {/* Preview Viewport */}
          {previewMode === 'visual' ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, minWidth: 0, maxWidth: '100%', overflowX: 'hidden' }}>
              {/* Simulated Gmail Message Box */}
              <div
                style={{
                  background: '#f8fafc',
                  borderRadius: 12,
                  padding: '16px 12px',
                  border: '1px solid #cbd5e1',
                  boxShadow: 'inset 0 2px 8px rgba(0,0,0,0.06)',
                  overflowX: 'auto',
                  maxWidth: '100%',
                  boxSizing: 'border-box',
                  WebkitOverflowScrolling: 'touch',
                }}
              >
                <div style={{ borderBottom: '1px solid #e2e8f0', paddingBottom: 10, marginBottom: 14, fontSize: 12, color: '#475569' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>
                      <strong>Od:</strong> {fullName} &lt;{email}&gt;
                    </span>
                    <span style={{ color: '#475569' }}>Dziś, 12:45</span>
                  </div>
                  <div style={{ color: '#1e293b', fontWeight: 600, marginTop: 4 }}>
                    Temat: Aktualizacja projektu CerebroSign &bull; Nowe szablony stopek
                  </div>
                </div>

                <div style={{ fontSize: 13, color: '#334155', lineHeight: 1.5, marginBottom: 18 }}>
                  Dzień dobry,<br /><br />
                  W załączeniu przesyłam zaktualizowany harmonogram wdrożenia oraz specyfikację techniczną integracji.<br />
                  W razie pytań pozostaję do dyspozycji.<br /><br />
                  Pozdrawiam serdecznie,
                </div>

                {/* Rendered HTML signature */}
                <div
                  style={{
                    width: '100%',
                    maxWidth: '100%',
                    minWidth: 0,
                    overflowX: 'auto',
                  }}
                  dangerouslySetInnerHTML={{ __html: generatedHtml }}
                />
              </div>
            </div>
          ) : (
            /* HTML Code Preview */
            <pre
              style={{
                background: '#f8fafc',
                border: '1px solid #e2e8f0',
                borderRadius: 8,
                padding: '14px',
                maxHeight: '440px',
                overflowY: 'auto',
                fontSize: 11,
                fontFamily: 'monospace',
                color: '#0066FF',
                lineHeight: 1.4,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-all',
              }}
            >
              {generatedHtml}
            </pre>
          )}

          {/* PRIMARY ACTION: INJECT TO GMAIL */}
          <div
            style={{
              padding: 14,
              background: '#f8fafc',
              borderRadius: 10,
              border: '1px solid #e2e8f0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 14,
              flexWrap: 'wrap',
            }}
          >
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#0B192C' }}>
                Wstrzyknij bezpośrednio do konta Gmail
              </div>
              <div style={{ fontSize: 11, color: '#475569' }}>
                Konto docelowe: <strong style={{ color: '#0066FF' }}>{email}</strong>
              </div>
            </div>

            <button
              onClick={handleDirectInjection}
              disabled={isInjecting}
              className="btn btn-primary"
              style={{ padding: '10px 22px', fontSize: 13, fontWeight: 700, borderRadius: 8 }}
            >
              {isInjecting ? <RefreshCw size={15} className="animate-spin" /> : <Send size={15} />}
              <span>{isInjecting ? 'Wstrzykiwanie do Gmail API...' : 'Wstrzyknij stopkę do mojego Gmaila'}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Modal: Zapisz jako szablon */}
      {isSaveModalOpen && (
        <div className="modal-overlay">
          <div className="modal-card" style={{ padding: 24 }}>
            <h3 style={{ fontSize: 18, fontWeight: 700, color: '#0B192C', marginBottom: 14 }}>
              Zapisz obecną stopkę jako szablon
            </h3>
            <p style={{ fontSize: 12, color: '#475569', marginBottom: 18 }}>
              Zapisany szablon będziesz mógł przypisać do działów firmy (np. Sprzedaż, Zarząd) lub wybranych pracowników.
            </p>

            <form onSubmit={handleSaveAsTemplate} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#cbd5e1', marginBottom: 6 }}>
                  Nazwa nowego szablonu
                </label>
                <input
                  type="text"
                  required
                  placeholder="np. Szablon Działu Handlowego 2026"
                  value={templateNameInput}
                  onChange={(e) => setTemplateNameInput(e.target.value)}
                  style={{
                    width: '100%',
                    background: '#f8fafc',
                    border: '1px solid #e2e8f0',
                    borderRadius: 8,
                    padding: '10px 14px',
                    color: '#0B192C',
                    fontSize: 13,
                  }}
                />
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 10 }}>
                <button
                  type="button"
                  onClick={() => setIsSaveModalOpen(false)}
                  style={{
                    background: 'transparent',
                    border: '1px solid #e2e8f0',
                    color: '#475569',
                    borderRadius: 8,
                    padding: '8px 16px',
                    fontSize: 13,
                    cursor: 'pointer',
                  }}
                >
                  Anuluj
                </button>
                <button
                  type="submit"
                  style={{
                    background: '#0284c7',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: 8,
                    padding: '8px 20px',
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  Zapisz szablon
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal: Zastosuj szablon do... */}
      {isApplyModalOpen && (
        <div className="modal-overlay">
          <div className="modal-card" style={{ padding: 24 }}>
            <h3 style={{ fontSize: 18, fontWeight: 700, color: '#0B192C', marginBottom: 14 }}>
              Zastosuj ten szablon w organizacji
            </h3>
            <p style={{ fontSize: 12, color: '#475569', marginBottom: 18 }}>
              Wybierz, do kogo ma zostać przypisany szablon: <strong style={{ color: '#0066FF' }}>{savedTemplates.find((t) => t.id === activeSavedTemplateId)?.name || 'Bieżący szablon'}</strong>.
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '10px 14px',
                  borderRadius: 8,
                  background: selectedApplyTarget === 'group' ? 'rgba(0, 102, 255, 0.08)' : '#ffffff',
                  border: selectedApplyTarget === 'group' ? '1.5px solid #0066FF' : '1px solid #e2e8f0',
                  cursor: 'pointer',
                }}
              >
                <input
                  type="radio"
                  name="applyTarget"
                  checked={selectedApplyTarget === 'group'}
                  onChange={() => setSelectedApplyTarget('group')}
                />
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#0B192C' }}>
                    Przypisz do grupy użytkowników
                  </div>
                  <div style={{ fontSize: 11, color: '#475569' }}>
                    Wszyscy obecni i nowi członkowie grupy otrzymają tę stopkę.
                  </div>
                </div>
              </label>

              {selectedApplyTarget === 'group' && (
                <div style={{ marginLeft: 26 }}>
                  <label style={{ display: 'block', fontSize: 11, color: '#475569', marginBottom: 6 }}>
                    Wybierz grupę:
                  </label>
                  <select
                    value={selectedApplyGroupId}
                    onChange={(e) => setSelectedApplyGroupId(e.target.value)}
                    style={{
                      width: '100%',
                      background: '#f1f5f9',
                      border: '1px solid #e2e8f0',
                      borderRadius: 8,
                      padding: '8px 12px',
                      color: '#0B192C',
                      fontSize: 13,
                    }}
                  >
                    {groups.map((grp) => (
                      <option key={grp.id} value={grp.id}>
                        {grp.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '10px 14px',
                  borderRadius: 8,
                  background: selectedApplyTarget === 'all' ? 'rgba(0, 102, 255, 0.08)' : '#ffffff',
                  border: selectedApplyTarget === 'all' ? '1.5px solid #0066FF' : '1px solid #e2e8f0',
                  cursor: 'pointer',
                }}
              >
                <input
                  type="radio"
                  name="applyTarget"
                  checked={selectedApplyTarget === 'all'}
                  onChange={() => setSelectedApplyTarget('all')}
                />
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#0B192C' }}>
                    Zastosuj do WSZYSTKICH pracowników domeny
                  </div>
                  <div style={{ fontSize: 11, color: '#475569' }}>
                    Ustaw jako domyślny szablon dla całej domeny {domain}.
                  </div>
                </div>
              </label>

              {activeUser && (
                <label
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: '10px 14px',
                    borderRadius: 8,
                    background: selectedApplyTarget === 'user' ? 'rgba(0, 102, 255, 0.08)' : '#ffffff',
                    border: selectedApplyTarget === 'user' ? '1.5px solid #0066FF' : '1px solid #e2e8f0',
                    cursor: 'pointer',
                  }}
                >
                  <input
                    type="radio"
                    name="applyTarget"
                    checked={selectedApplyTarget === 'user'}
                    onChange={() => setSelectedApplyTarget('user')}
                  />
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#0B192C' }}>
                      Zastosuj tylko do pracownika: {activeUser.fullName}
                    </div>
                    <div style={{ fontSize: 11, color: '#475569' }}>
                      Indywidualna stopka przypisana do {activeUser.primaryEmail}.
                    </div>
                  </div>
                </label>
              )}

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 12 }}>
                <button
                  type="button"
                  onClick={() => setIsApplyModalOpen(false)}
                  style={{
                    background: 'transparent',
                    border: '1px solid #e2e8f0',
                    color: '#475569',
                    borderRadius: 8,
                    padding: '8px 16px',
                    fontSize: 13,
                    cursor: 'pointer',
                  }}
                >
                  Anuluj
                </button>
                <button
                  type="button"
                  onClick={handleConfirmApply}
                  style={{
                    background: '#10b981',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: 8,
                    padding: '8px 20px',
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  Potwierdź przypisanie
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
